import * as THREE from "three";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import debug from "debug";

const log = debug("app:gsgd");

/**
 * Class for detecting collisions between objects in a scene and updating part numbers based on the collision state.
 * @category Experience
 * @subcategory ObjectManagement
 */
class DetectCollisionGsg {
  /**
   * Constructs a DetectCollisionGsg instance.
   * @param {Object} experience - The main application experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   * @param {Object} experience.portVault - The port vault map containing port data.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;
    this.eventHub = this.experience.eventHub;
    this.portVault = this.experience.portVault.portVaultMap;
    this.objects = [];
    this.setEventListeners();
  }


  /**
   * Sets up event listeners for collision detection events.
   */
  setEventListeners() {
    this.eventHub.on("gsg-collision", this.init.bind(this));
  }

  /**
   * Initializes collision detection by creating bounding boxes, applying materials, positioning meshes,
   * detecting collisions, updating part numbers, and emitting events with the results.
   * @param {Object} xmd - The input data containing figures for collision detection.
   */
  init(xmd) {
    try {
      log("input data", xmd);
      this.createBoundingBoxes(xmd); // Ensure this is called first
      this.applyMaterials(this.objects); // Use this.objects
      log("applied materials", this.objects);
      this.positionMeshes(this.objects);
      const collisionDetails = this.detectCollisions();
      const finalXMD = this.updatePartNumbers(collisionDetails, xmd);

      this.addMeshesToScene(this.objects);
      this.cleanup(); // Clean up resources
      this.eventHub.emit("MakePartsEvent", {
        eventType: "auto",
        data: finalXMD,
      });
    } catch (error) {
      log("Error during collision detection:", error);
    }
  }

  /**
   * Creates bounding boxes for objects based on the input data.
   * @param {Object} xmd - The input data containing figures for collision detection.
   */
  createBoundingBoxes(xmd) {
    if (!xmd) {
      this.cleanup();
    }

    this.objects = xmd.figures.map((figure, index) => {
      const group = new THREE.Group();
      this.scene.add(group);
      return {
        figure,
        group,
        id: index,
        parent: figure.partData.parent,
        collisionSides: { left: false, right: false }, // Initialize collision sides
        hasCollision: false, // Initialize collision flag
      };
    });

    log("created initial objects", this.objects);
  }

  /**
   * Applies materials to the objects for visualizing collision detection results.
   * @param {Array} objects - The objects to which materials are applied.
   */
  applyMaterials(objects) {
    objects.forEach((obj, index) => {
      const opacity = index % 2 === 0 ? 0.75 : 0.25;
      log("obj", obj.parent, index, opacity);

      const materialLeft = new THREE.MeshBasicMaterial({
        color: 0xff0000, // Red
        transparent: true,
        opacity: opacity,
      });
      const materialRight = new THREE.MeshBasicMaterial({
        color: 0x0000ff, // Blue
        transparent: true,
        opacity: opacity,
      });

      // Create sphere geometries instead of rectangles
      const geometry = new THREE.SphereGeometry(0.45, 32, 32);
      const meshLeft = new THREE.Mesh(geometry, materialLeft);
      const meshRight = new THREE.Mesh(geometry, materialRight);

      const h = index % 2 === 0 ? 0.5 : 0.5;

      // Position spheres where the rectangles were
      meshLeft.position.set(-1.5, 3, h);
      meshRight.position.set(1.5, 3, h);

      meshLeft.name = "l";
      meshRight.name = "r";

      obj.group.add(meshLeft, meshRight);

      // Removed green geometry entirely
    });
  }

  /**
   * Positions meshes based on their respective figure positions and angles.
   * @param {Array} objects - The objects to be positioned in the scene.
   */
  positionMeshes(objects) {
    objects.forEach((obj) => {
      obj.group.position.set(obj.figure.x, 2, obj.figure.z);
      obj.group.rotation.y = THREE.MathUtils.degToRad(obj.figure.angle);
    });
  }

  /**
   * Adds meshes to the scene for visualization purposes.
   * @param {Array} objects - The objects to be added to the scene.
   */
  addMeshesToScene(objects) {
    objects.forEach((obj) => {
      this.scene.add(obj.group);
    });
  }

  /**
   * Detects collisions between objects and updates their collision status.
   * @returns {Array} The objects with updated collision details.
   */
  detectCollisions() {
    this.objects.forEach((obj1, index1) => {
      this.objects.forEach((obj2, index2) => {
        if (index1 !== index2) {
          const bboxGroup1 = new THREE.Box3().setFromObject(obj1.group);
          const bboxGroup2 = new THREE.Box3().setFromObject(obj2.group);

          if (bboxGroup1.intersectsBox(bboxGroup2)) {
            this.checkMeshCollisions(obj1, obj2);
          }
        }
      });
    });

    return this.objects;
  }

  /**
   * Checks for collisions between individual meshes of two objects and updates their collision states.
   * @param {Object} obj1 - The first object to check for collisions.
   * @param {Object} obj2 - The second object to check for collisions.
   */
  checkMeshCollisions(obj1, obj2) {
    log("objects", obj1, obj2);
    const meshLeft1 = obj1.group.children.find((mesh) => mesh.name === "l");
    const meshRight1 = obj1.group.children.find((mesh) => mesh.name === "r");
    const meshLeft2 = obj2.group.children.find((mesh) => mesh.name === "l");
    const meshRight2 = obj2.group.children.find((mesh) => mesh.name === "r");

    const bboxLeft1 = new THREE.Box3().setFromObject(meshLeft1);
    const bboxRight1 = new THREE.Box3().setFromObject(meshRight1);
    const bboxLeft2 = new THREE.Box3().setFromObject(meshLeft2);
    const bboxRight2 = new THREE.Box3().setFromObject(meshRight2);

    if (bboxLeft1.intersectsBox(bboxRight2)) {
      obj1.collisionSides.left = true;
      obj2.collisionSides.right = true;
    } else if (bboxLeft1.intersectsBox(bboxLeft2)) {
      obj1.collisionSides.left = true;
      obj2.collisionSides.left = true;
    } else if (bboxRight1.intersectsBox(bboxRight2)) {
      obj1.collisionSides.right = true;
      obj2.collisionSides.right = true;
    } else if (bboxRight1.intersectsBox(bboxLeft2)) {
      obj1.collisionSides.right = true;
      obj2.collisionSides.left = true;
    }

    obj1.hasCollision = obj1.collisionSides.left || obj1.collisionSides.right;
    obj2.hasCollision = obj2.collisionSides.left || obj2.collisionSides.right;
  }

  /**
   * Updates the part numbers of objects based on their collision states.
   * @param {Array} collisionDetails - The objects with updated collision details.
   * @param {Object} xmd - The input data containing figures.
   * @returns {Object} The updated xmd data with modified part numbers.
   */
  updatePartNumbers(collisionDetails, xmd) {
    collisionDetails.forEach((collisionInfo) => {
      const figure = collisionInfo.figure;
      const { left, right } = collisionInfo.collisionSides;

      if (!collisionInfo.hasCollision) {
        figure.partData.fullpartnumber = "XM-ANCHOR2";
      } else if (left && right) {
        figure.partData.fullpartnumber = "XM-ANCHOR2-AA";
      } else if (left) {
        figure.partData.fullpartnumber = "XM-ANCHOR2-A(L)";
      } else if (right) {
        figure.partData.fullpartnumber = "XM-ANCHOR2-A(R)";
      } else {
        log("Unhandled case in collision detection", collisionInfo);
      }
    });

    return xmd;
  }

  /**
   * Cleans up resources by removing objects from the scene and disposing of their geometry and materials.
   */
  cleanup() {
    this.objects.forEach((obj) => {
      obj.group.traverse((child) => {
        if (child.isMesh) {
          child.geometry.dispose();
          child.material.dispose();
        }
      });
      this.scene.remove(obj.group);
    });
    this.objects = [];
  }
}

export default DetectCollisionGsg;
